/**
 * spiceVisitNote — pure builders for "the email is the note" (the SPICE
 * email summary opens in the note composer as the session note) and the
 * decline form's note.
 *
 * No fetches, no React: the note text, the refusal text, the band a
 * visit starts on, and the SPICE hours default all live here so they
 * are unit-testable independent of the form that calls them. The note's
 * invoice line comes from its read (api/_shared/repos/noteExtraction.ts),
 * the same as a care-management note's; a decline's is the server's
 * fixed SPICE_DECLINE_INVOICE_LINE.
 *
 * SPICE_VISIT_RATE_BANDS mirrors api/_shared/bandVisibility.ts's
 * SPICE_NOTE_BANDS (the FE cannot import api/_shared; the
 * cmHomeApi.ts / noteVocabulary.ts / capabilities.ts mirror discipline).
 */

import { proseDate } from '@/lib/proseDate';

export const SPICE_BAND = 'spice';
export const SPICE_FRIENDLY_VISITOR_BAND = 'spice_friendly_visitor';

/** The two SPICE rate bands (SPICE_NOTE_BANDS mirror, see header). */
export const SPICE_VISIT_RATE_BANDS = [SPICE_BAND, SPICE_FRIENDLY_VISITOR_BAND] as const;
export type SpiceVisitRateBand = (typeof SPICE_VISIT_RATE_BANDS)[number];

/** A SPICE session is 1 hour by default (founder ruling: "SPICE hours
 *  are default 1 but can be edited"); the decline form's note carries it
 *  as the composer's starting hours. */
export const SPICE_VISIT_DEFAULT_HOURS = 1;

/**
 * The hours the note composer's box starts on for a band, while the
 * person has not typed there: '1' on the SPICE band, blank on every other
 * band (Friendly Visitor bills the actual door-to-door time, which the
 * therapist enters, like any time-based band). Always editable; the
 * server holds no SPICE hours rule.
 */
export function spiceDefaultHoursForBand(band: string): string {
  return band === SPICE_BAND ? String(SPICE_VISIT_DEFAULT_HOURS) : '';
}

/**
 * The band a SPICE visit's time starts on, from the kinds of the
 * client's scheduled sessions (a session's kind is 'spice' or
 * 'spice_friendly_visitor', the same values as the two bands): Friendly
 * Visitor when the client has sessions and every one is a Friendly
 * Visitor session; SPICE otherwise (no schedule, no sessions, all SPICE,
 * or a mix). The person can always change it for the visit.
 */
export function defaultSpiceBandForSessionKinds(kinds: readonly string[]): SpiceVisitRateBand {
  return kinds.length > 0 && kinds.every((k) => k === SPICE_FRIENDLY_VISITOR_BAND)
    ? SPICE_FRIENDLY_VISITOR_BAND
    : SPICE_BAND;
}

/**
 * The SPICE email summary becomes the note body: a first-line stamp
 * (plain text, house voice) recording that this is the SPICE email
 * summary and the date it was sent, then the email exactly as sent. The
 * email is the note; nothing is pasted in separately.
 */
export function buildSpiceVisitNoteText(input: { emailBody: string; sentOnIso: string }): string {
  const stamp = `SPICE email summary sent ${proseDate(input.sentOnIso)}.`;
  return `${stamp}\n\n${input.emailBody.trim()}`;
}

export interface SpiceDeclineFacts {
  whatWasTried: string;
  whatTheySaid: string;
  timeOfDay: string;
}

/**
 * A refusal is still a session: the note text is built from the three
 * required facts, plain text, no email involved.
 */
export function buildSpiceDeclineNoteText(facts: SpiceDeclineFacts): string {
  return [
    "The client declined today's SPICE visit.",
    `What was tried: ${facts.whatWasTried.trim()}`,
    `What they said: ${facts.whatTheySaid.trim()}`,
    `Time of day: ${facts.timeOfDay.trim()}`,
  ].join('\n');
}

/** Whether the three refusal facts are complete enough to file (each a
 *  non-empty string after trimming). Pure; the form uses this to enable
 *  its submit button. */
export function spiceDeclineFactsAreComplete(facts: SpiceDeclineFacts): boolean {
  return (
    facts.whatWasTried.trim().length > 0 &&
    facts.whatTheySaid.trim().length > 0 &&
    facts.timeOfDay.trim().length > 0
  );
}
